import React from "react";
import { useState, useEffect, useRef } from "react";
import Header from "../components/BuilderComponents/Header/Header";
import Sidebar, {
  SidebarMenus,
} from "../components/BuilderComponents/Sidebar/Sidebar";
import BuilderContent from "../components/BuilderComponents/BuilderContent/BuilderContent";
import { setActivePage } from "../store/reducers/builderReducers/pages/pagesActions";
import WebsiteAPI from "../store/reducers/builderReducers/website/websiteApi";
import TypographiesAPI from "../store/reducers/builderReducers/typographies/typographiesApi";
import NotificationContainer from "react-notifications/lib/NotificationContainer";
import PagesAPI from "../store/reducers/builderReducers/pages/pagesApi";
import LanguageSettingsAPI from "../store/reducers/builderReducers/languageSettings/languageSettingsApi";
import FontOptionsAPI from "../store/reducers/builderReducers/font_options/font_optionsApi";
import FontUrlAPI from "../store/reducers/builderReducers/font_url/font_urlApi";
import ColorAPI from "../store/reducers/builderReducers/color/colorApi";
import { updateEditor } from "../store/reducers/builderReducers/editor/editorActions";
import type { Editor } from "../store/reducers/builderReducers/editor/editorTypes";
import {
  editorIdsFromComponent,
  resolveEditorComponentStructureKey,
} from "../store/reducers/builderReducers/editor/editorSelectionHelpers";
import { useAppSelector } from "../store/hooks";
import { Pages } from "../store/reducers/builderReducers/pages/pagesApi";
import {
  inClass,
  getStoredActivePageSelection,
  validateStoredPageExists,
  clearStoredActivePageSelection,
  resolveStoredSelectionToWebpageUuid,
} from "../utils/Helpers";
import {
  addNewComponent,
  deleteComponentById,
  updateStructureComponent,
} from "../store/reducers/builderReducers/structure/structureActions";
import { setEditableComponentId } from "../store/reducers/textEditor/textEditorActions";
import TemplateApi from "../store/reducers/builderReducers/templates/templateApi";
import { useEditor } from "@tiptap/react";
import { extensions } from "../components/_default/utilities/customContentTags";
import SaveConfirmation from "../components/BuilderComponents/Editors/Confirmation/SaveConfirmation";
import TemplateEditOverlay from "../components/BuilderComponents/Editors/TemplateEditOverlay/TemplateEditOverlay";
import { FileLinkPickerProvider } from "../components/BuilderComponents/Editors/Editor/TextEditorComponent/FileLinkPickerContext";
import { Plugin, PluginKey } from "prosemirror-state";
import {
  getComponentFromStructureMap,
  getComponentIdentityKey,
} from "../components/_default/utilities/componentIdentity";
import type { AllComponents } from "../components/_default/interfaces/base";
import { setSentryWebsiteName } from "../sentry";

/** Matches DOM `id` / contentEditable selection when structure uses UUID or legacy numeric id. */
function resolveEditableTargetId(
  c: AllComponents | undefined
): string | number | null {
  if (!c) return null;
  const u = c.uuid?.trim();
  if (u) return u;
  if (c.id != null && c.id !== undefined) return c.id;
  return null;
}

export const editorId = -11;

export default function Builder() {
  const [sidebarActive, toggleSidebar] = useState(true);
  const [selectedMenu, setSelectedMenu] = useState<SidebarMenus>("");
  const [editPageId, setEditPageId] = useState<number | null>(null);
  const components = useAppSelector(
    (state) => state.builder.structure.components
  ) as any;
  const editorComponent = getComponentFromStructureMap(components, editorId);
  // const components = structure.components;
  const pages = useAppSelector((state) => state.builder.pages.list);
  const activePageId = useAppSelector(
    (state) => state.builder.pages.active as Pages["active"]
  );

  const builderEditor = useAppSelector((state) => state.builder.editor as Editor);
  const editableComponentId = useAppSelector((state) =>
    state.textEditor?.get?.("id") ?? state.textEditor?.id ?? null
  );

  const colors = useAppSelector((state) => state.builder.colors);
  const typographies = useAppSelector((state) => state.builder.typographies);
  const website = useAppSelector((state) => state.builder.website);

  useEffect(() => {
    setSentryWebsiteName(website?.name);
  }, [website?.name]);


  const activeComponentKey = useAppSelector((state) => {
    const textEditorId = state.textEditor?.get?.("id") ?? state.textEditor?.id;
    const textEditorComponent = getComponentFromStructureMap(
      state.builder.structure.components,
      textEditorId
    );
    if (textEditorComponent) {
      return getComponentIdentityKey(textEditorComponent);
    }
    return resolveEditorComponentStructureKey(
      state.builder.structure.components,
      state.builder.editor as Editor
    );
  });

  const activeStructureKeyRef = useRef<string | null>(null);
  activeStructureKeyRef.current = activeComponentKey;
  const previousActiveComponentKeyRef = useRef<string | null>(null);
  const componentsRef = useRef<any>(components);
  componentsRef.current = components;

  /**
   * When TipTap onUpdate writes to structure, Redux updates re-run the sync effect below.
   * Re-applying HTML via DOMParser + setContent collapses/normalizes spaces (and fights the
   * editor), which feels like needing two spaces for one. Skip one sync for the key we just wrote.
   */
  const skipEditorSyncFromOwnUpdateRef = useRef<string | null>(null);

  const [content, setContent] = useState("");

  const editor = useEditor({
    extensions,
    content,

    onUpdate({ editor }) {
      const html = editor.getHTML();
      setContent(html);

      const key = activeStructureKeyRef.current;
      if (key) {
        const targetComponent = componentsRef.current?.[key];
        const isTextComponent = ["ContentComponent", "Button", "nav-item", "text"].includes(
          targetComponent?.type
        );
        if (!isTextComponent) {
          return;
        }

        const previousText = String(targetComponent?.attributes?.text ?? "");
        const isHtmlEmpty = html.trim() === "" || html.replace(/\s/g, "") === "<p></p>";
        if (isHtmlEmpty && previousText.trim() !== "") {
          return;
        }
        if (previousText === html) {
          return;
        }

        skipEditorSyncFromOwnUpdateRef.current = key;
        updateStructureComponent(
          key,
          "attributes.text",
          html,
          true
        );
      }
    },

    onCreate: ({ editor }) => {
      // Clear history on load to prevent first undo from happening
      // editor.commands.
      // Temporarily reset content to clear history
      const initialContent = editor.getHTML();
      editor.commands.setContent("<p></p>");
      editor.commands.setContent(initialContent);

      // Plugin to detect and block unintended content clears on undo
      // editor.registerPlugin(
      //   new Plugin({
      //     key: new PluginKey("preventClearOnUndo"),
      //     filterTransaction: (transaction, state) => {
      //       console.log(
      //         transaction,
      //         state,
      //         "THESE ARE THE TRANSACTIONS AND STATE"
      //       );

      //       // Check if transaction is undo by comparing the transaction's doc state
      //       const isUndo =
      //         transaction.getMeta("addToHistory") === false &&
      //         undo(state, editor.view.dispatch); // Detect if it’s an undo by simulating one
      //       console.log("Transaction type: ", isUndo ? "Undo" : "Other");

      //       if (!isUndo) return true; // Allow non-undo transactions

      //       // Check if undo would leave only a trailing break
      //       const docAfterTransaction = transaction.doc;
      //       const isEffectivelyEmpty =
      //         docAfterTransaction.childCount === 1 &&
      //         docAfterTransaction.firstChild?.type.name === "paragraph" &&
      //         docAfterTransaction.firstChild.childCount === 0;

      //       console.log("Is effectively empty: ", isEffectivelyEmpty);
      //       return !isEffectivelyEmpty; // Block if it would clear content
      //     },
      //   })
      // );

      // Add custom paste handling
      editor.registerPlugin(
        new Plugin({
          key: new PluginKey("plainTextPaste"),
          props: {
            handlePaste: (view, event) => {
              event.preventDefault(); // Prevent the default paste behavior

              // Check if clipboardData is available
              const clipboardData = event.clipboardData;
              if (clipboardData) {
                // Get plain text from clipboard
                const text = clipboardData.getData("text/plain");
                // Insert plain text into the editor
                const { state, dispatch } = view;
                const transaction = state.tr.insertText(text);
                dispatch(transaction);
              } else {
                console.warn("Clipboard data is not available.");
              }

              return true; // Prevent the default handling (if needed)
            },
          },
        })
      );
    },
  });

  // On first load
  useEffect(() => {
    PagesAPI.loadWebpages();
    TypographiesAPI.loadTypographies();
    FontUrlAPI.getFontUrl();
    FontOptionsAPI.getFontOptions();
    ColorAPI.loadColors();
    WebsiteAPI.loadWebsite();
    LanguageSettingsAPI.loadLanguageSettings();
    TemplateApi.loadTemplates();
  }, []);

  useEffect(() => {
    if (!builderEditor.hasEdited) {
      return;
    }

    const onBeforeUnload = (e: BeforeUnloadEvent) => {
      e.preventDefault();
      e.returnValue = "";
    };

    window.addEventListener("beforeunload", onBeforeUnload);
    return () => window.removeEventListener("beforeunload", onBeforeUnload);
  }, [builderEditor.hasEdited]);

  useEffect(() => {
    const activeKey = activeComponentKey ? String(activeComponentKey) : null;
    const activeComponentChanged =
      previousActiveComponentKeyRef.current !== activeKey;
    previousActiveComponentKeyRef.current = activeKey;

    if (activeComponentKey) {
      if (
        components[activeComponentKey]?.type === "ContentComponent" ||
        components[activeComponentKey]?.type === "Button" ||
        components[activeComponentKey]?.type === "nav-item" ||
        components[activeComponentKey]?.type === "text"
      ) {
        const content = components[activeComponentKey]?.attributes?.text ?? "<p></p>";

        // Check if content contains <font> and if it does, the the attributes

        if (content !== undefined && content !== null) {
          const htmlString = String(content);
          // Create a new DOM parser
          // Create a new DOM parser
          const parser = new DOMParser();

          // Parse the HTML string
          const doc = parser.parseFromString(htmlString, "text/html");

          // Find all font elements
          const fontElements = doc.querySelectorAll("font");

          // Iterate over each font element
          fontElements.forEach((fontElement) => {
            // Create a new <p> element
            const spanElement = document.createElement("span");
            // Copy the innerHTML of the font element to the <p> element
            spanElement.innerHTML = fontElement.innerHTML;

            // Preserve style attribute of the font element
            const styleAttribute = fontElement.getAttribute("style");
            if (styleAttribute !== null) {
              spanElement.setAttribute("style", styleAttribute);
            }

            const colorAttribute = fontElement.getAttribute("color");

            if (colorAttribute) {
              let spanStyle = spanElement.getAttribute("style") || "";

              if (!spanStyle.includes("color")) {
                // Add color property to style
                spanStyle += `color: ${colorAttribute}; `;
              } else {
                // Update existing color property
                spanStyle = spanStyle.replace(
                  /color:[^;]+;/,
                  `color: ${colorAttribute};`
                );
              }
              spanElement.setAttribute("style", spanStyle);
            }

            const parentNode = fontElement.parentNode;

            if (parentNode !== null) {
              parentNode.replaceChild(spanElement, fontElement);
            }
          });

          if (components[activeComponentKey]?.type === "Button") {
            const brElements = doc.querySelectorAll("br");
            brElements.forEach((brElement) => {
              brElement.parentNode?.removeChild(brElement);
            });
          }

          const modifedHtml = doc.body.innerHTML;

          if (skipEditorSyncFromOwnUpdateRef.current === activeComponentKey) {
            skipEditorSyncFromOwnUpdateRef.current = null;
            return;
          }
          if (skipEditorSyncFromOwnUpdateRef.current != null) {
            skipEditorSyncFromOwnUpdateRef.current = null;
          }

          setContent((previousContent) =>
            previousContent === modifedHtml ? previousContent : modifedHtml
          );

          if (editor && editor.getHTML() !== modifedHtml) {
            // Avoid replacing the live doc while typing in the same target.
            // This prevents ProseMirror DOM mapping from drifting out of sync.
            if (activeComponentChanged || !editor.isFocused) {
              editor.commands.setContent(modifedHtml, false);
            }
          }
        }
      }
    }
  }, [activeComponentKey, components, editor, editableComponentId]);

  function handleClick(e: React.MouseEvent<HTMLElement>) {
    let clickedElement = e.target as HTMLElement;
    const inTemplateOverlay = Boolean(
      clickedElement.closest("#template_edit_over_lay")
    );
    if (
      !builderEditor.template &&
      editorComponent &&
      clickedElement.classList.contains("builder-component--active")
    ) {
      deleteComponentById(editorId);
      setEditableComponentId(null);
    }

    const builderComponent = inClass("builder-component", clickedElement);
    const domBuilderIdRaw = builderComponent?.id?.trim?.() ?? "";
    const builderElementComponent = builderComponent
      ? getComponentFromStructureMap(components, builderComponent.id)
      : undefined;
    const builderSelectionUuid = builderElementComponent?.uuid?.trim() || null;
    const builderSelectionId =
      builderElementComponent?.id != null
        ? builderElementComponent.id
        : domBuilderIdRaw && /^\d+$/.test(domBuilderIdRaw)
          ? Number(domBuilderIdRaw)
          : null;
    const builderSelectionKey =
      resolveEditableTargetId(builderElementComponent) ??
      (domBuilderIdRaw || null);

    if (builderComponent) {

      if (
        builderElementComponent &&
        ((builderElementComponent as any).type === "ContentComponent" ||
          builderElementComponent.type === "Button" ||
          builderElementComponent.type === "nav-item" ||
          builderElementComponent.type === "text")
      ) {
        const htmlString = (getComponentFromStructureMap(
          components,
          builderComponent.id
        )?.attributes as any)?.text;
        // Create a new DOM parser
        // Create a new DOM parser
        const parser = new DOMParser();

        // Parse the HTML string
        const doc = parser.parseFromString(htmlString, "text/html");

        // Find all font elements
        const fontElements = doc.querySelectorAll("font");

        // Iterate over each font element
        fontElements.forEach((fontElement) => {
          // Create a new <p> element
          const spanElement = document.createElement("span");
          // Copy the innerHTML of the font element to the <p> element
          spanElement.innerHTML = fontElement.innerHTML;

          // Preserve style attribute of the font element
          const styleAttribute = fontElement.getAttribute("style");
          if (styleAttribute !== null) {
            spanElement.setAttribute("style", styleAttribute);
          }

          const colorAttribute = fontElement.getAttribute("color");

          if (colorAttribute) {
            let spanStyle = spanElement.getAttribute("style") || "";

            if (!spanStyle.includes("color")) {
              // Add color property to style
              spanStyle += `color: ${colorAttribute}; `;
            } else {
              // Update existing color property
              spanStyle = spanStyle.replace(
                /color:[^;]+;/,
                `color: ${colorAttribute};`
              );
            }
            spanElement.setAttribute("style", spanStyle);
          }

          const parentNode = fontElement.parentNode;

          if (parentNode !== null) {
            parentNode.replaceChild(spanElement, fontElement);
          }
        });

        const modifedHtml = doc.body.innerHTML;

        setContent(modifedHtml);

        //editor?.commands.setContent(modifedHtml);
      }

      if (builderElementComponent) {
        updateEditor({
          hasEdited: builderEditor.hasEdited,
          ...editorIdsFromComponent(builderElementComponent, components),
          template:
            builderEditor && builderEditor.template
              ? builderEditor.template
              : false,
          editor: editor,
        });
      }
    }

    const clickIsInsideBuilderComponent = !!builderComponent;
    const clickIsEditorUi =
      !!inClass("global-editor", clickedElement) ||
      !!inClass("bubble-menu", clickedElement);
    const clickIsBuilderDropdown =
      !!inClass("react-select-container", clickedElement) ||
      !!inClass("builder-dropdown", clickedElement);
    const clickIsModal = !!inClass("modal", clickedElement);

    if (clickIsInsideBuilderComponent && builderSelectionKey) {
      const sentinelParentUuid =
        typeof (editorComponent as any)?.parent_uuid === "string"
          ? (editorComponent as any).parent_uuid.trim()
          : "";
      const sentinelParentId =
        (editorComponent as any)?.parent_id != null
          ? String((editorComponent as any).parent_id)
          : "";
      const sentinelParentKey = sentinelParentUuid || sentinelParentId;
      const clickedIsActive = clickedElement.classList.contains(
        "builder-component--active"
      );
      const shouldRecreateSentinel =
        !editorComponent ||
        sentinelParentKey !== String(builderSelectionKey);

      const willAddSentinel =
        shouldRecreateSentinel &&
        (!clickedIsActive || !!builderEditor.template || !editorComponent);


      // In template overlay, stale active class can exist without sentinel in structure.
      if (willAddSentinel) {
        if (editorComponent) {
          deleteComponentById(editorId);
        }
        addNewComponent({
          id: editorId,
          uuid: null,
          type: "editor",
          sort_order: 1,
          parent_id: builderSelectionUuid ? null : builderSelectionId,
          parent_uuid: builderSelectionUuid,
          childElements: [],
          childWebpageComponentRelations: [],
          version: 0,
          webpage_id: pages[0].id,
          attributes: {
            content_width_size: 0,
            content_width_unit: "px",
            classes: "",
            styles: {},
            typography_id: 1,
          },
        } as any);
      }
      // Keep selected component identity in textEditor for active class and
      // drag targeting across all component types in template overlay.
      setEditableComponentId(builderSelectionKey);
      return;
    }


    if (!clickIsEditorUi && !clickIsBuilderDropdown && !clickIsModal) {
      unsetEditElementRef(e);
      if (editorComponent) {
        deleteComponentById(editorId);
      }
    }
  }

  function unsetEditElementRef(e: React.MouseEvent<HTMLElement>) {
    const component = inClass("builder-component", e.target as HTMLElement);
    const modal = inClass("modal", e.target as HTMLElement);
    if (!component && !modal) {
      setEditableComponentId(null);
    }
  }

  // When pages are loaded, get current pages components
  useEffect(() => {
    if (pages && pages.length) {
      // if ( activePageId && activePageId !== prevActivePageId.current) {

      //   prevActivePageId.current = activePageId
      //   StructureAPI.loadWebpageComponents(activePageId)
      // } else if (!components.length) {

      //   StructureAPI.loadWebpageComponents(pages[0].id)
      //   prevActivePageId.current = pages[0].id
      //   setActivePage(pages[0].id)
      // }
      if (!activePageId) {
        const stored = getStoredActivePageSelection();

        if (
          stored !== null &&
          validateStoredPageExists(stored, pages)
        ) {
          const uuid = resolveStoredSelectionToWebpageUuid(stored, pages);
          if (uuid) {
            setActivePage(uuid);
          }
        } else {
          if (stored !== null) {
            clearStoredActivePageSelection();
          }

          setActivePage(pages[0].uuid);
        }
      }
    }
  }, [pages]);

  useEffect(() => {
    if (colors) {
      const shopThemeColor = colors.find((x) => x.name === "SHOPTHEMECOLOR");

      if (shopThemeColor?.color) {
        document.documentElement.style.setProperty(
          "--shop-theme-color",
          shopThemeColor?.color
        );
      }
    }
  }, [colors]);

  useEffect(() => {
    if (colors) {
      const shopThemeColor = colors.find(
        (x) => x.name === "SHOPTHEMECOLORHOVER"
      );

      if (shopThemeColor?.color) {
        document.documentElement.style.setProperty(
          "--shop-theme-color-hover",
          shopThemeColor?.color
        );
      }
    }
  }, [colors]);

  useEffect(() => {
    if (website) {
      document.documentElement.style.setProperty(
        "--shop-border-radius",
        website.shopBRadius ? website.shopBRadius : "0px"
      );
    }
  }, [website]);

  useEffect(() => {
    if (typographies) {
      const fontFamily = typographies.filter((x) => x.tag === "p")[0];

      if (fontFamily?.fontFamily) {
        document.documentElement.style.setProperty(
          "--shop-font-family",
          fontFamily.fontFamily
        );
      }
    }
  }, [typographies]);

  return (
    <FileLinkPickerProvider>
      <div
        className="d-flex flex-column h-100 w-100"
        onMouseDown={(e) => handleClick(e)}
      >
        <NotificationContainer />
        <Header
          sidebarActive={sidebarActive}
          toggleSidebar={toggleSidebar}
          setSelectedMenu={setSelectedMenu}
          setEditPageId={setEditPageId}
        />
        <div className="app-layout">
          {sidebarActive && (
            <Sidebar
              selectedMenu={selectedMenu}
              setSelectedMenu={setSelectedMenu}
              editPageId={editPageId}
              setEditPageId={setEditPageId}
            />
          )}
          <div className="app-content" id="app-content">
            <BuilderContent></BuilderContent>
            <SaveConfirmation />
          </div>
          <TemplateEditOverlay />
        </div>
      </div>
    </FileLinkPickerProvider>
  );
}
